import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Scissors,
  Phone,
  MapPin,
  Clock,
  Instagram,
  Star,
  ArrowUpRight,
  ArrowDown,
} from "lucide-react";
import ProjectTopBar from "@/components/ProjectTopBar";
import Seo from "@/components/Seo";

// ── Direction artistique : cuir, laiton, oxblood, grain ──────────────
const OX = "#8E2B21"; // oxblood
const BRASS = "#C79A4B"; // laiton
const CREAM = "#EDE4D6";
const INK = "#0b0a09";

const ease = [0.22, 1, 0.36, 1];

function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.8, delay, ease }}
    >
      {children}
    </motion.div>
  );
}

const services = [
  { n: "01", name: "Coupe Signature", price: "28€", time: "40 min", desc: "Consultation, coupe ciseaux & tondeuse, coiffage. Le classique de la maison." },
  { n: "02", name: "Coupe + Barbe", price: "42€", time: "55 min", desc: "Coupe complète, taille au rasoir, serviette chaude et soin. L'expérience totale." },
  { n: "03", name: "Taille de Barbe", price: "20€", time: "30 min", desc: "Contours au rasoir, huile et baume. Une barbe nette, dessinée, entretenue." },
  { n: "04", name: "Rasage Traditionnel", price: "30€", time: "40 min", desc: "Coupe-chou, serviette chaude, after-shave. Le rituel à l'ancienne." },
  { n: "05", name: "Coupe Enfant", price: "18€", time: "30 min", desc: "Coupe adaptée, patience et bonne humeur pour les plus jeunes." },
  { n: "06", name: "Le Grand Jeu", price: "60€", time: "1h30", desc: "Coupe + barbe + rasage + soin visage. Le forfait maison, sans compromis." },
];

const gallery = [
  "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800&h=1000&fit=crop&q=80",
  "https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=800&h=600&fit=crop&q=80",
  "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=800&h=1000&fit=crop&q=80",
  "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=800&h=600&fit=crop&q=80",
  "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=800&h=1000&fit=crop&q=80",
  "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=800&h=600&fit=crop&q=80",
];

// Bande diagonale style "barber pole"
function PoleStripe({ className = "" }) {
  return (
    <div
      aria-hidden
      className={className}
      style={{
        backgroundImage: `repeating-linear-gradient(45deg, ${OX} 0 14px, ${INK} 14px 28px, ${BRASS} 28px 42px, ${INK} 42px 56px)`,
      }}
    />
  );
}

const nav = [
  ["Maison", "top"],
  ["Prestations", "prestations"],
  ["Galerie", "galerie"],
  ["Avis", "avis"],
  ["Contact", "contact"],
];

export default function Barbier() {
  const aboutRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: aboutRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Seo title="La Maison du Barbier — concept barbier" description="Exemple de site pour un barbier : prestations, ambiance et réservation. Concept ML. Agence Web." />
      <ProjectTopBar />
      <div
        className="min-h-screen font-sans selection:bg-[#8E2B21] selection:text-white"
        style={{ background: INK, color: CREAM }}
      >
        {/* Grain */}
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-[1] opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        <PoleStripe className="h-1.5 w-full sticky top-0 z-40" />

        {/* Nav */}
        <nav className="sticky top-1.5 z-40 backdrop-blur-md" style={{ background: "rgba(11,10,9,0.82)", borderBottom: `1px solid ${BRASS}22` }}>
          <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <a href="#top" onClick={scrollTo("top")} className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: OX }}>
                <Scissors className="w-4 h-4" style={{ color: CREAM }} />
              </span>
              <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ color: CREAM }}>
                Maison du Barbier
              </span>
            </a>
            <div className="hidden md:flex items-center gap-8">
              {nav.map(([label, id]) => (
                <a key={id} href={`#${id}`} onClick={scrollTo(id)} className="text-[11px] uppercase tracking-[0.2em] transition-colors" style={{ color: `${CREAM}99` }}>
                  {label}
                </a>
              ))}
            </div>
            <a href="#contact" onClick={scrollTo("contact")} className="text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-2 rounded-full" style={{ background: BRASS, color: INK }}>
              Réserver
            </a>
          </div>
        </nav>

        {/* HERO */}
        <section id="top" className="relative z-10 min-h-[92vh] flex flex-col justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=1920&h=1200&fit=crop&q=80" alt="" className="w-full h-full object-cover" style={{ opacity: 0.32 }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${INK}cc, ${INK}88 45%, ${INK})` }} />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-3 mb-8">
              <span className="h-px w-10" style={{ background: BRASS }} />
              <span className="text-[11px] uppercase tracking-[0.35em]" style={{ color: BRASS }}>
                Barbershop · Depuis 1998 · Lyon
              </span>
            </motion.div>

            <h1 className="font-black uppercase leading-[0.82] tracking-[-0.02em]" style={{ fontSize: "clamp(3.5rem, 12vw, 10rem)" }}>
              {["La coupe,", "un rituel."].map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span
                    className="inline-block"
                    style={i === 1 ? { color: BRASS, fontStyle: "italic" } : {}}
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.95, ease, delay: 0.15 + i * 0.12 }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }} className="md:col-span-5 text-base leading-relaxed" style={{ color: `${CREAM}aa` }}>
                Barbershop traditionnel au cœur de Lyon. Coupe, barbe et rasage au coupe-chou, dans une ambiance cuir et laiton où l'on prend le temps.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }} className="md:col-span-7 flex flex-wrap gap-4 md:justify-end">
                <a href="#contact" onClick={scrollTo("contact")} className="px-8 h-14 inline-flex items-center gap-2 rounded-full font-bold text-sm uppercase tracking-wide transition-transform hover:scale-105" style={{ background: OX, color: CREAM }}>
                  Réserver un créneau <ArrowUpRight className="w-4 h-4" />
                </a>
                <a href="#prestations" onClick={scrollTo("prestations")} className="px-8 h-14 inline-flex items-center rounded-full font-medium text-sm uppercase tracking-wide border" style={{ borderColor: `${BRASS}55`, color: CREAM }}>
                  Voir les prestations
                </a>
              </motion.div>
            </div>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="absolute bottom-6 left-1/2 -translate-x-1/2" style={{ color: `${BRASS}` }}>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
              <ArrowDown className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </section>

        {/* Marquee services */}
        <div className="relative z-10 overflow-hidden py-5 border-y" style={{ borderColor: `${BRASS}22`, background: "#100e0c" }}>
          <motion.div className="flex whitespace-nowrap gap-10" animate={{ x: ["0%", "-50%"] }} transition={{ repeat: Infinity, duration: 22, ease: "linear" }}>
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-10 pr-10">
                {["Coupe Signature", "Rasage au coupe-chou", "Taille de barbe", "Serviette chaude", "Soin visage", "Coupe enfant", "Le Grand Jeu"].map((w) => (
                  <span key={w + k} className="flex items-center gap-4 text-2xl font-black uppercase tracking-tight" style={{ color: `${CREAM}` }}>
                    {w} <span style={{ color: OX }}>✦</span>
                  </span>
                ))}
              </div>
            ))}
          </motion.div>
        </div>

        {/* About + parallax */}
        <section ref={aboutRef} className="relative z-10 max-w-6xl mx-auto px-6 py-28 grid md:grid-cols-2 gap-16 items-center">
          <Reveal className="order-2 md:order-1">
            <div className="relative rounded-sm overflow-hidden" style={{ border: `1px solid ${BRASS}33` }}>
              <div className="aspect-[3/4] overflow-hidden">
                <motion.img style={{ y: imgY }} src="https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=900&h=1200&fit=crop&q=80" alt="Barbier au travail" className="w-full h-[115%] object-cover" />
              </div>
              <div className="absolute bottom-4 left-4 px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-bold" style={{ background: BRASS, color: INK }}>
                Estd. 1998
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="order-1 md:order-2">
            <span className="text-[11px] uppercase tracking-[0.35em]" style={{ color: BRASS }}>La maison</span>
            <h2 className="font-black uppercase leading-[0.9] mt-4 mb-6" style={{ fontSize: "clamp(2rem,4.5vw,3.5rem)" }}>
              Un métier,<br /><span style={{ color: OX, fontStyle: "italic" }}>un caractère.</span>
            </h2>
            <p className="leading-relaxed mb-5" style={{ color: `${CREAM}aa` }}>
              Depuis plus de 25 ans, la Maison du Barbier perpétue le geste juste. Ici, on prend le temps : une coupe, une conversation, un verre. Le soin du détail, à l'ancienne.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {[["25+", "ans de métier"], ["4,9★", "sur Google"], ["6j/7", "sur rendez-vous"]].map(([k, v]) => (
                <div key={v}>
                  <div className="font-black text-3xl" style={{ color: BRASS }}>{k}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] mt-1" style={{ color: `${CREAM}77` }}>{v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Prestations */}
        <section id="prestations" className="relative z-10 max-w-6xl mx-auto px-6 py-24">
          <Reveal>
            <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
              <h2 className="font-black uppercase leading-[0.9]" style={{ fontSize: "clamp(2rem,5vw,4rem)" }}>
                Presta<span style={{ color: OX }}>tions</span>
              </h2>
              <span className="text-[11px] uppercase tracking-[0.25em]" style={{ color: `${CREAM}77` }}>Grille tarifaire · sans rendez-vous possible</span>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: `${BRASS}22` }}>
            {services.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease }}
                className="group p-8 transition-colors"
                style={{ background: INK }}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-mono text-xs" style={{ color: BRASS }}>{s.n}</span>
                  <div className="text-right">
                    <div className="font-black text-2xl" style={{ color: CREAM }}>{s.price}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: `${CREAM}66` }}>{s.time}</div>
                  </div>
                </div>
                <h3 className="font-black uppercase tracking-tight text-xl mb-2 transition-colors" style={{ color: CREAM }}>{s.name}</h3>
                <p className="text-sm leading-relaxed" style={{ color: `${CREAM}99` }}>{s.desc}</p>
                <div className="mt-5 h-px w-0 group-hover:w-full transition-all duration-500" style={{ background: OX }} />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Galerie */}
        <section id="galerie" className="relative z-10 max-w-6xl mx-auto px-6 py-24">
          <Reveal>
            <h2 className="font-black uppercase leading-[0.9] mb-14" style={{ fontSize: "clamp(2rem,5vw,4rem)" }}>
              Le <span style={{ color: BRASS, fontStyle: "italic" }}>salon</span>
            </h2>
          </Reveal>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
            {gallery.map((img, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
                <div className="overflow-hidden rounded-sm group" style={{ border: `1px solid ${BRASS}22` }}>
                  <img src={img} alt={`Salon ${i + 1}`} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Avis */}
        <section id="avis" className="relative z-10 py-24" style={{ background: "#100e0c", borderTop: `1px solid ${BRASS}22`, borderBottom: `1px solid ${BRASS}22` }}>
          <div className="max-w-6xl mx-auto px-6">
            <Reveal><h2 className="font-black uppercase leading-[0.9] mb-14 text-center" style={{ fontSize: "clamp(2rem,4.5vw,3.5rem)" }}>Ils reviennent</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { n: "Karim T.", t: "Le seul endroit où ma barbe est vraiment bien taillée. On se sent chez soi dès qu'on passe la porte." },
                { n: "Julien P.", t: "Le rasage au coupe-chou, la serviette chaude… un autre niveau. Je ne vais plus ailleurs." },
                { n: "Marco L.", t: "Accueil, café, coupe nickel. Un vrai barbershop comme on n'en fait plus." },
              ].map((r, i) => (
                <Reveal key={r.n} delay={i * 0.1}>
                  <div className="p-8 h-full rounded-sm" style={{ background: INK, border: `1px solid ${BRASS}22` }}>
                    <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4" fill={BRASS} color={BRASS} />)}</div>
                    <p className="italic leading-relaxed mb-6" style={{ color: `${CREAM}cc` }}>“{r.t}”</p>
                    <p className="font-bold uppercase tracking-wide text-sm" style={{ color: BRASS }}>{r.n}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Contact / Réservation */}
        <section id="contact" className="relative z-10 max-w-6xl mx-auto px-6 py-28">
          <div className="grid md:grid-cols-2 gap-16">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.35em]" style={{ color: BRASS }}>Réserver</span>
              <h2 className="font-black uppercase leading-[0.88] mt-4 mb-8" style={{ fontSize: "clamp(2.2rem,5vw,4rem)" }}>
                Prenez place<br /><span style={{ color: OX, fontStyle: "italic" }}>dans le fauteuil.</span>
              </h2>
              <div className="space-y-5">
                {[
                  { icon: <Phone className="w-5 h-5" />, label: "Téléphone", val: "04 78 00 00 00" },
                  { icon: <MapPin className="w-5 h-5" />, label: "Adresse", val: "12 rue de la République, Lyon" },
                  { icon: <Clock className="w-5 h-5" />, label: "Horaires", val: "Lun – Sam · 9h – 20h" },
                  { icon: <Instagram className="w-5 h-5" />, label: "Instagram", val: "@maison.du.barbier" },
                ].map((x) => (
                  <div key={x.label} className="flex items-center gap-4">
                    <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: `${BRASS}18`, color: BRASS }}>{x.icon}</span>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: `${CREAM}66` }}>{x.label}</div>
                      <div className="text-lg" style={{ color: CREAM }}>{x.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <form onSubmit={(e) => e.preventDefault()} className="p-8 rounded-sm" style={{ background: "#100e0c", border: `1px solid ${BRASS}33` }}>
                <div className="grid gap-5">
                  <input placeholder="Prénom & nom" className="px-4 py-3 rounded-sm outline-none" style={{ background: INK, border: `1px solid ${BRASS}33`, color: CREAM }} />
                  <input placeholder="Téléphone" className="px-4 py-3 rounded-sm outline-none" style={{ background: INK, border: `1px solid ${BRASS}33`, color: CREAM }} />
                  <select className="px-4 py-3 rounded-sm outline-none" style={{ background: INK, border: `1px solid ${BRASS}33`, color: CREAM }}>
                    {services.map((s) => <option key={s.name}>{s.name} — {s.price}</option>)}
                  </select>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input type="date" className="px-4 py-3 rounded-sm outline-none" style={{ background: INK, border: `1px solid ${BRASS}33`, color: CREAM }} />
                    <input type="time" className="px-4 py-3 rounded-sm outline-none" style={{ background: INK, border: `1px solid ${BRASS}33`, color: CREAM }} />
                  </div>
                  <button className="mt-2 py-4 rounded-full font-bold uppercase tracking-wide transition-transform hover:scale-[1.02]" style={{ background: OX, color: CREAM }}>
                    Confirmer la réservation
                  </button>
                  <p className="text-center text-[10px] uppercase tracking-[0.2em]" style={{ color: `${CREAM}55` }}>Confirmation par SMS · Annulation gratuite 24h avant</p>
                </div>
              </form>
            </Reveal>
          </div>
        </section>

        <PoleStripe className="h-1.5 w-full" />
        <footer className="relative z-10 py-10 text-center" style={{ background: INK }}>
          <div className="font-black uppercase tracking-[0.2em] text-lg mb-2" style={{ color: CREAM }}>
            Maison du Barbier
          </div>
          <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: `${CREAM}55` }}>
            © 2026 — Concept <span style={{ color: BRASS }}>ML.</span> Agence
          </p>
        </footer>
      </div>
    </>
  );
}
